/**
 * Push service-worker SCOPE GUARD — reaches EXISTING generated apps via the CDN
 * platform.js (issue #3705 / #4770 / #4200, three confirmed customers)
 *
 * THE BUG
 * -------
 * A generated app with the PWA plugin AND `overskill.push` registered TWO
 * different service-worker scripts on scope "/":
 *
 *   src/lib/pwa-update.ts  → new Workbox("/sw.js")               precache, NO push
 *   overskill-sdk          → register("/sw-push.js", {scope:"/"}) push, NO precache
 *
 * A service-worker registration is keyed by SCOPE, so registering a different
 * script URL at an existing scope REPLACES that registration. The two clobbered
 * each other on every page load. When the Workbox worker won — which it reliably
 * does on an installed Android home-screen PWA — the push event was delivered to
 * a worker with no `push` listener, and Android rendered its own generic "Tap to
 * copy the URL for this app" notification instead of the app's branded one. The
 * relay reported the send as delivered, so nothing we monitor ever failed.
 *
 * WHY THE FIX HAS TO LIVE HERE
 * ----------------------------
 * The template fix (vite.config.ts pulls the push handlers into the ONE Workbox
 * worker with `workbox.importScripts`) only covers apps generated from now on.
 * The clobbering `register()` call itself lives in `overskill-sdk`, an npm
 * dependency PINNED in every app's package-lock.json — an already-deployed app
 * runs 0.5.6 and cannot be changed without a rebuild.
 *
 * `platform.js` is different: every existing app already loads
 * `https://sdk.overskill.com/v1/platform.js`, it is a `defer` script in <head> so
 * it evaluates BEFORE the app's module bundle, and the CDN's V1_LATEST pointer
 * updates it fleet-wide with no per-app redeploy. So this module wraps
 * `navigator.serviceWorker.register` and makes the old SDK's
 * `register("/sw-push.js", {scope:"/"})` REUSE the push-capable worker that
 * already owns "/" instead of replacing it.
 *
 * INVARIANTS
 * ----------
 *  - Intercept ONLY `/sw-push.js` at root scope. Every other registration —
 *    including `/sw.js` itself — passes straight through to the native method.
 *  - Delegate ONLY to a registration that PROVES it handles push, by answering
 *    the ping. Handing back a bare precache worker would silently re-create the
 *    #3705 bug from the other side, which is worse than the clobber.
 *  - Any doubt (no handoff, no active worker, no pong, a throw, a timeout) falls
 *    through to the native `register()`. The guard can only ever be a no-op.
 *  - Never install twice; never throw into the host app.
 */

/** Page → worker: "can you handle push?" */
export const PUSH_SW_PING = 'OVERSKILL_PUSH_SW_PING';

/** Worker → page: "yes". ANY pong counts; the payload is diagnostic. */
export const PUSH_SW_PONG = 'OVERSKILL_PUSH_SW_PONG';

/**
 * Where the template's src/lib/pwa-update.ts publishes the app's ONE
 * registration, synchronously at module-eval time. Kept in lockstep with
 * `SERVICE_WORKER_HANDOFF_KEY` there.
 */
export const SERVICE_WORKER_HANDOFF_KEY = '__overskillServiceWorker';

/** The script overskill-sdk registers, and the only one this guard intercepts. */
export const PUSH_SW_PATH = '/sw-push.js';

/** Marks the container so a second platform.js eval cannot double-wrap. */
export const GUARD_INSTALLED_KEY = '__overskillPushScopeGuard';

/**
 * The container's ORIGINAL `register`, saved when the guard wraps it. A caller
 * that has ALREADY run `resolveRootPushRegistration()` (the SDK banner's own
 * subscribe) calls this instead of the wrapped `register`, so the resolution
 * — and its ping waits — runs once, not twice, before the permission prompt.
 */
export const NATIVE_REGISTER_KEY = '__overskillNativeRegister';

/**
 * A worker that is going to answer does so in a single event-loop turn, so this
 * budget only has to cover a worker that will NEVER answer.
 *
 * ⚠️ THE WHOLE RESOLUTION RUNS BEFORE `Notification.requestPermission()`.
 * Safari ties the permission prompt to transient user activation, and an
 * overlong wait here would silently suppress the prompt — burning the user's one
 * gesture, which is exactly the hazard push-client.ts already warns about for the
 * VAPID fetch. Worst case is bounded at HANDOFF + ACTIVATION + PING ≈ 2.8s: the
 * handoff wait, then waiting for a first-visit worker to activate, then the ping.
 *
 * The trade-off runs the other way too, and this is the deliberate direction: if
 * a first-install worker takes longer than this to activate, we give up and
 * register `/sw-push.js`, which costs the precache until the next page load
 * (`pwa-update.ts` re-registers `/sw.js` then, so it self-heals). Losing a
 * permission prompt does not self-heal. Prefer the recoverable failure.
 */
export const PING_TIMEOUT_MS = 800;

/** Bounds the wait on the template handoff so a never-settling promise can't hang subscribe(). */
export const HANDOFF_TIMEOUT_MS = 1200;

interface ServiceWorkerHandoff {
  scriptPath?: string;
  scope?: string;
  registration?: Promise<ServiceWorkerRegistration | undefined>;
}

/** Absolute-or-relative URL → pathname, or null when it cannot be resolved. */
export function scriptPathname(scriptURL: unknown): string | null {
  try {
    if (scriptURL instanceof URL) return scriptURL.pathname;
    if (typeof scriptURL !== 'string' || scriptURL.length === 0) return null;
    const base =
      typeof location !== 'undefined' && location.href ? location.href : 'https://localhost/';
    return new URL(scriptURL, base).pathname;
  } catch {
    return null;
  }
}

/**
 * True when a `register()` call is asking for the push worker at ROOT scope —
 * the one and only case this guard acts on.
 *
 * An OMITTED scope counts: `register("/sw-push.js")` defaults to the script's
 * own directory, which for a root-level script is "/". That default is how the
 * collision happens even when a caller passes no options at all.
 */
export function targetsRootPushWorker(scriptURL: unknown, options?: unknown): boolean {
  if (scriptPathname(scriptURL) !== PUSH_SW_PATH) return false;
  const rawScope = (options as { scope?: unknown } | undefined)?.scope;
  if (rawScope === undefined || rawScope === null) return true;
  return scriptPathname(rawScope) === '/';
}

/**
 * Ask a service worker whether it handles push. Resolves `false` — never
 * rejects — on a timeout, a missing worker, or any throw.
 */
export function pingPushCapability(
  worker: ServiceWorker | null | undefined,
  timeoutMs: number = PING_TIMEOUT_MS
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (!worker || typeof worker.postMessage !== 'function') {
      resolve(false);
      return;
    }

    let settled = false;
    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event: MessageEvent) => {
        const data = event.data as { type?: string } | null;
        if (timer !== undefined) clearTimeout(timer);
        settle(!!data && data.type === PUSH_SW_PONG);
      };
      timer = setTimeout(() => settle(false), timeoutMs);
      worker.postMessage({ type: PUSH_SW_PING }, [channel.port2]);
    } catch {
      if (timer !== undefined) clearTimeout(timer);
      settle(false);
    }
  });
}

/**
 * Only an ACTIVE worker can answer a message, and on a FIRST visit the
 * registration we were handed is still `installing` — so waiting for activation
 * is required, not an optimization. Without it, a `subscribe()` early in a first
 * page load would read the brand-new worker as push-incapable and register
 * `/sw-push.js` straight over it, which is the #3705 collision again.
 *
 * `navigator.serviceWorker.ready` is the standard primitive for "a registration
 * with an active worker exists for this page"; it never rejects, and the timeout
 * bounds a registration that never activates.
 */
async function activeWorker(
  registration: ServiceWorkerRegistration,
  timeoutMs: number
): Promise<ServiceWorker | null> {
  if (registration.active) return registration.active;
  try {
    const ready = await withTimeout(navigator.serviceWorker.ready, timeoutMs);
    return registration.active ?? ready?.active ?? null;
  } catch {
    return registration.active ?? null;
  }
}

async function registrationHandlesPush(
  registration: ServiceWorkerRegistration | null | undefined,
  timeoutMs: number
): Promise<boolean> {
  if (!registration) return false;
  const worker = await activeWorker(registration, timeoutMs);
  return pingPushCapability(worker, timeoutMs);
}

function readHandoff(): ServiceWorkerHandoff | null {
  try {
    const handoff = (window as unknown as Record<string, unknown>)[
      SERVICE_WORKER_HANDOFF_KEY
    ] as ServiceWorkerHandoff | undefined;
    if (!handoff || typeof handoff !== 'object') return null;
    return handoff;
  } catch {
    return null;
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | undefined> {
  return new Promise<T | undefined>((resolve) => {
    let settled = false;
    const settle = (value: T | undefined) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => settle(undefined), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        settle(value);
      },
      () => {
        clearTimeout(timer);
        settle(undefined);
      }
    );
  });
}

/**
 * The registration that owns root scope AND has proven it receives push, or
 * null when there is no such registration (the caller must then register its
 * own worker — the pre-existing behavior).
 *
 * Two sources, in order:
 *   1. The template handoff. Preferred because it is published SYNCHRONOUSLY
 *      before `wb.register()` settles, so a `subscribe()` during app startup
 *      waits for the incoming registration instead of concluding there is none
 *      and racing it.
 *   2. `getRegistration("/")` — covers an app that has no handoff (deployed
 *      before it existed) but does have a live root-scope worker.
 */
export async function resolveRootPushRegistration(
  opts: { pingTimeoutMs?: number; handoffTimeoutMs?: number } = {}
): Promise<ServiceWorkerRegistration | null> {
  const pingTimeoutMs = opts.pingTimeoutMs ?? PING_TIMEOUT_MS;
  const handoffTimeoutMs = opts.handoffTimeoutMs ?? HANDOFF_TIMEOUT_MS;

  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;

    const handoff = readHandoff();
    if (handoff?.registration && typeof handoff.registration.then === 'function') {
      const registration = await withTimeout(handoff.registration, handoffTimeoutMs);
      if (registration && typeof registration === 'object') {
        if (await registrationHandlesPush(registration, pingTimeoutMs)) {
          return registration as ServiceWorkerRegistration;
        }
        // A handoff that resolved to a worker which does NOT handle push means
        // the app's /sw.js predates the fix and its Worker has not been
        // refreshed. Registering /sw-push.js is then the ONLY way this device
        // renders a notification, and losing precache is the cheaper failure.
        return null;
      }
      // The handoff timed out, rejected, or resolved to something that is not a
      // registration (a slow `wb.register()` update, a failed one). That proves
      // NOTHING about the worker already owning "/" — returning null here let
      // the guard natively register /sw-push.js over a push-capable /sw.js,
      // which is the #3705 clobber this module exists to stop (SW-250 fleet
      // review, reproduced). Fall through to the live root lookup instead.
    }

    const existing = await navigator.serviceWorker.getRegistration('/');
    if (await registrationHandlesPush(existing, pingTimeoutMs)) {
      return existing as ServiceWorkerRegistration;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Wrap `navigator.serviceWorker.register` so a `/sw-push.js` registration at
 * root scope reuses the push-capable worker that already owns "/".
 *
 * @returns true when the wrapper was installed (false = unsupported, already
 *   installed, or explicitly disabled — all no-ops).
 */
export function installPushScopeGuard(opts: { enabled?: boolean } = {}): boolean {
  if (opts.enabled === false) return false;
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
    const container = navigator.serviceWorker as ServiceWorkerContainer &
      Record<string, unknown>;
    if (!container || typeof container.register !== 'function') return false;
    if (container[GUARD_INSTALLED_KEY] === true) return false;

    const native = container.register.bind(container);

    const guarded = function (
      scriptURL: string | URL,
      options?: RegistrationOptions
    ): Promise<ServiceWorkerRegistration> {
      try {
        if (!targetsRootPushWorker(scriptURL, options)) {
          return native(scriptURL, options);
        }
        return resolveRootPushRegistration().then(
          (existing) => existing ?? native(scriptURL, options),
          // resolveRootPushRegistration already swallows everything, but a
          // rejection here must still never break a caller's subscribe().
          () => native(scriptURL, options)
        );
      } catch {
        return native(scriptURL, options);
      }
    };

    container.register = guarded as ServiceWorkerContainer['register'];
    container[GUARD_INSTALLED_KEY] = true;
    try {
      container[NATIVE_REGISTER_KEY] = native;
    } catch {
      // Unwritable — callers fall back to the guarded register (two waits).
    }
    return true;
  } catch {
    return false;
  }
}
